import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { applicationStatusLabel, canApplyForJobs, isEmployer } from "../utils/userRole";

const API_URL = getApiBaseUrl();

/**
 * Applicant view: track / withdraw job applications.
 */
export default function MyApplications({ user }) {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API_URL}/get_my_applications.php?applicant_id=${user.id}`
      );
      if (!res.data?.success) {
        setError(res.data?.message || "Failed to load applications.");
        setApps([]);
        return;
      }
      setApps(res.data.applications || []);
    } catch (err) {
      setError(err?.message || "Network error.");
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const withdraw = async (application) => {
    if (!window.confirm("Withdraw this application?")) return;
    setBusyId(application.id);
    try {
      const res = await axios.post(`${API_URL}/withdraw_application.php`, {
        application_id: application.id,
        applicant_id: user.id,
      });
      if (!res.data?.success) {
        alert(res.data?.message || "Could not withdraw.");
        return;
      }
      await load();
    } catch (err) {
      alert(err?.message || "Network error.");
    } finally {
      setBusyId(null);
    }
  };

  if (!user) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        Please log in to view your applications.
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: 820,
        margin: "0 auto",
        padding: "24px 16px 64px",
        minHeight: "100vh",
        background: "#f8fafc",
      }}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: "none",
          background: "transparent",
          color: "#0f766e",
          cursor: "pointer",
          marginBottom: 12,
          padding: 0,
        }}
      >
        ← Back
      </button>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ margin: "0 0 6px", fontSize: 28, color: "#0f172a" }}>
            My Applications
          </h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
            Track status for jobs you have applied to.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {isEmployer(user) && (
            <Link
              to="/hiring/applications"
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                background: "#0f766e",
                color: "#fff",
                textDecoration: "none",
                fontWeight: 650,
                fontSize: 13,
              }}
            >
              Employer inbox
            </Link>
          )}
          {canApplyForJobs(user) && (
            <Link
              to="/home"
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                border: "1px solid #0f766e",
                color: "#0f766e",
                textDecoration: "none",
                fontWeight: 650,
                fontSize: 13,
                background: "#fff",
              }}
            >
              Browse jobs
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</div>
      )}

      {loading ? (
        <p style={{ color: "#64748b" }}>Loading…</p>
      ) : apps.length === 0 ? (
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 36,
            textAlign: "center",
            color: "#64748b",
          }}
        >
          You have not applied to any jobs yet.
          <div style={{ marginTop: 12 }}>
            <Link to="/home" style={{ color: "#0f766e", fontWeight: 600 }}>
              Find hiring posts on Home
            </Link>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {apps.map((a) => {
            const canWithdraw =
              a.status !== "withdrawn" &&
              a.status !== "hired" &&
              a.status !== "offered" &&
              a.status !== "rejected";
            return (
              <div
                key={a.id}
                style={{
                  background: "#fff",
                  borderRadius: 14,
                  border: "1px solid #e2e8f0",
                  padding: 16,
                  display: "flex",
                  gap: 14,
                  flexWrap: "wrap",
                }}
              >
                {a.cover_image ? (
                  <img
                    src={a.cover_image}
                    alt=""
                    style={{
                      width: 88,
                      height: 66,
                      objectFit: "cover",
                      borderRadius: 10,
                    }}
                  />
                ) : (
                  <img
                    src={resolveAvatarUrl(a.employer)}
                    onError={handleAvatarError}
                    alt=""
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 200 }}>
                  <Link
                    to={`/home/hiring/${a.post_id}`}
                    style={{
                      fontWeight: 750,
                      fontSize: 16,
                      color: "#0f172a",
                      textDecoration: "none",
                    }}
                  >
                    {a.job_title || "Job posting"}
                  </Link>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                    {[a.hiring_company, a.hiring_location]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                    Applied{" "}
                    {a.created_at
                      ? new Date(a.created_at).toLocaleString()
                      : ""}
                  </div>
                  {a.interview_at && a.status === "interview_scheduled" && (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#0f766e",
                        marginTop: 6,
                        fontWeight: 600,
                      }}
                    >
                      Interview: {new Date(a.interview_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      padding: "4px 10px",
                      borderRadius: 8,
                      background:
                        a.status === "rejected" || a.status === "withdrawn"
                          ? "#fef2f2"
                          : a.status === "hired" || a.status === "offered"
                            ? "#ecfdf5"
                            : "#f1f5f9",
                      color:
                        a.status === "rejected" || a.status === "withdrawn"
                          ? "#b91c1c"
                          : "#0f766e",
                    }}
                  >
                    {applicationStatusLabel(a.status)}
                  </span>
                  {canWithdraw && (
                    <button
                      type="button"
                      disabled={busyId === a.id}
                      onClick={() => withdraw(a)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#94a3b8",
                        fontSize: 12,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      {busyId === a.id ? "Withdrawing…" : "Withdraw"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
